import React, { useEffect, useState } from 'react';
import PageHeader from './PageHeader';
import { API } from '../apiClient';

function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function fmt(amount) {
  if (amount == null) return '0,00 PLN';
  return Number(amount).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' PLN';
}

const MONTH_NAMES = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonth(monthStr) {
  const [y, m] = monthStr.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

function formatQuarter(period) {
  const [year, quarter] = period.split('-');
  return `${quarter} ${year}`;
}

function formatPeriodLabel(period, mode) {
  if (!period) return '—';
  if (mode === 'months') return formatMonth(period);
  if (mode === 'quarters') return formatQuarter(period);
  return period;
}

function prevMonth(monthStr) {
  const [y, m] = monthStr.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

function nextMonth(monthStr) {
  const [y, m] = monthStr.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

function toneForRecommendation(recommendation) {
  if (recommendation === 'obniż / promuj') return { bg: '#fff7ed', border: '#fdba74', text: '#9a3412' };
  if (recommendation === 'podnieś / chroń cenę') return { bg: '#ecfdf5', border: '#86efac', text: '#166534' };
  return { bg: '#f8fafc', border: '#cbd5e1', text: '#334155' };
}

function chartPoints(rows, maxValue, width = 520, height = 180) {
  if (!rows.length) return '';
  const stepX = rows.length === 1 ? 0 : width / (rows.length - 1);
  return rows.map((item, idx) => {
    const x = rows.length === 1 ? width / 2 : idx * stepX;
    const y = height - ((Number(item.revenue || 0) / Math.max(maxValue, 1)) * (height - 16)) - 8;
    return `${x},${Math.max(8, Math.min(height - 8, y))}`;
  }).join(' ');
}

function progressPct(value, target) {
  if (!target || target <= 0) return 0;
  return Math.max(0, Math.min(100, (Number(value || 0) / Number(target)) * 100));
}

export default function Finance() {
  return <FinanceInner />;
}

function FinanceInner() {
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();

  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [historyMode, setHistoryMode] = useState('months');

  useEffect(() => {
    load(month);
  }, [month]);

  async function load(m) {
    setLoading(true);
    setError('');
    try {
      const d = await api(`/finance/summary?month=${m}`);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function openEdit() {
    if (!data) return;
    const cats = [
      { category: 'mortgage', label: 'Kredyt', defaultAmount: 1483 },
      { category: 'rent', label: 'Czynsz', defaultAmount: 568 },
      { category: 'electricity', label: 'Prąd', defaultAmount: 320 },
      { category: 'gas', label: 'Gaz', defaultAmount: 300 },
    ];
    const existing = {};
    (data.details?.fixed_items || []).forEach((i) => { existing[i.category] = i.amount; });
    setEditItems(cats.map((c) => ({ category: c.category, label: c.label, amount: existing[c.category] ?? c.defaultAmount })));
    setEditOpen(true);
  }

  async function saveEdit() {
    setEditSaving(true);
    try {
      for (const item of editItems) {
        const amount = parseFloat(String(item.amount).replace(',', '.'));
        if (isNaN(amount) || amount <= 0) continue;
        await api('/finance/fixed-costs', {
          method: 'POST',
          body: JSON.stringify({ category: item.category, description: item.label, amount, month }),
        });
      }
      setEditOpen(false);
      await load(month);
    } catch (e) {
      setError(e.message);
    } finally {
      setEditSaving(false);
    }
  }

  const mariaCostPerBooking = Number(data?.details?.maria_cost_per_booking || 100);
  const cleaningJobCount = Number(data?.details?.cleaning_count || 0);
  const cleaningCost = Number(data?.cleaning || 0);

  if (loading) return <section><PageHeader icon="📊" title="Finanse" description="Ładowanie..." /><p className="muted" style={{ padding: '2rem' }}>Ładowanie danych finansowych...</p></section>;
  if (error) return <section><PageHeader icon="📊" title="Finanse" description="Błąd ładowania" /><div className="error" style={{ padding: '1rem' }}>{error}</div></section>;

  const net = data?.net_income || 0;
  const isPositive = net >= 0;
  const analytics = data?.analytics || {};
  const history = analytics?.history || {};
  const historyRows = history?.[historyMode] || [];
  const maxHistoryRevenue = Math.max(1, ...historyRows.map((item) => Number(item.revenue || 0)));
  const averages = analytics?.averages || {};
  const pricingPatterns = analytics?.pricing_patterns || {};
  const pricingEngine = pricingPatterns?.pricing_engine || {};
  const marketInsights = pricingEngine?.market_insights || {};
  const futurePricing = pricingEngine?.future_pricing || [];
  const pricingRules = pricingEngine?.rules || [];
  const revenuePlan = analytics?.revenue_plan || {};
  const monthPatterns = pricingPatterns?.month_patterns || [];
  const weekdayPatterns = pricingPatterns?.weekday_patterns || [];
  const weakMonths = monthPatterns.filter((item) => item.recommendation === 'obniż / promuj').slice(0, 3);
  const strongMonths = monthPatterns.filter((item) => item.recommendation === 'podnieś / chroń cenę').slice(0, 3);
  const weakWeekdays = weekdayPatterns.filter((item) => item.recommendation === 'obniż / promuj').slice(0, 3);
  const strongWeekdays = weekdayPatterns.filter((item) => item.recommendation === 'podnieś / chroń cenę').slice(0, 3);
  const coverage = analytics?.coverage;
  const currentHistoryMonth = (history?.months || []).find((item) => item.period === month);
  const selectedRevenue = Number(data?.revenue || currentHistoryMonth?.revenue || 0);
  const selectedRevenueNet = Number(data?.revenue_net || currentHistoryMonth?.revenue_net || 0);
  const selectedBookings = Number(data?.details?.bookings?.length || currentHistoryMonth?.bookings || 0);
  const baselineTarget = Number(revenuePlan?.baseline_target || averages?.avg_monthly_revenue || 0);
  const stretchTarget = Number(revenuePlan?.stretch_target || 0);
  const selectedGapToAverage = Math.max(baselineTarget - selectedRevenue, 0);
  const selectedGapToStretch = Math.max(stretchTarget - selectedRevenue, 0);
  const avgBookingValue = selectedBookings ? selectedRevenue / selectedBookings : 0;
  const projectedMariaCost = selectedBookings * 100;
  const revenueChartPoints = chartPoints(historyRows, maxHistoryRevenue);

  const bestRevenuePeriod = historyRows.reduce((best, item) => !best || Number(item.revenue || 0) > Number(best.revenue || 0) ? item : best, null);
  const worstOccupancyPeriod = historyRows.reduce((worst, item) => !worst || Number(item.occupancy_pct || 0) < Number(worst.occupancy_pct || 0) ? item : worst, null);
  const visibleHistoryRows = historyRows.slice(-6);
  const primaryDecision = futurePricing.find((item) => item.period >= month) || futurePricing[0];
  const primaryDecisionUp = Number(primaryDecision?.price_delta_pct || 0) > 0;
  const primaryDecisionDown = Number(primaryDecision?.price_delta_pct || 0) < 0;
  const decisionTone = primaryDecisionUp
    ? { bg: '#ecfdf5', border: '#86efac', text: '#166534', icon: '↗' }
    : primaryDecisionDown
      ? { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', icon: '↘' }
      : { bg: '#f8fafc', border: '#cbd5e1', text: '#334155', icon: '→' };

  return (
    <section>
      <PageHeader
        icon="📊"
        title="Finanse"
        description={`Podsumowanie miesiąca: ${formatMonth(month)}`}
        helpKey="finance"
        stats={[
          { label: 'Przychód netto', value: fmt(data?.revenue_net), tone: 'info' },
          { label: 'Koszty razem', value: fmt(data?.costs_total), tone: 'warning' },
          { label: 'Wynik', value: fmt(net), tone: isPositive ? 'success' : 'danger' },
        ]}
        actions={
          <>
            <button type="button" className="secondary" onClick={() => setMonth(prevMonth(month))} title="Poprzedni miesiąc">«</button>
            <button type="button" className="secondary" onClick={() => setMonth(nextMonth(month))} title="Następny miesiąc">»</button>
          </>
        }
      />

      <div className="finance-hero" style={{
        display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: '1rem',
        padding: '1rem', borderRadius: 18,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #2563eb 100%)',
        color: 'white', marginBottom: '1rem', boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
      }}>
        <div style={{ padding: '0.75rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0.35rem 0.65rem', borderRadius: 999, background: 'rgba(255,255,255,0.12)', fontSize: '0.82rem', fontWeight: 700 }}>
            {isPositive ? 'Miesiąc na plusie' : 'Miesiąc pod presją'}
          </div>
          <div style={{ fontSize: '2.8rem', lineHeight: 1.05, fontWeight: 950, marginTop: '0.8rem', letterSpacing: '-0.04em' }}>
            {isPositive ? '+' : ''}{fmt(net)}
          </div>
          <div style={{ color: '#cbd5e1', marginTop: '0.35rem' }}>
            {isPositive ? 'zysk netto' : 'strata netto'} w {formatMonth(month)} po kosztach stałych, Marii i prowizji.
          </div>
          <div className="finance-kpi-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.6rem', marginTop: '1rem' }}>
            <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '0.7rem' }}>
              <div style={{ color: '#cbd5e1', fontSize: '0.78rem' }}>Przychód</div>
              <strong>{fmt(data?.revenue)}</strong>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '0.7rem' }}>
              <div style={{ color: '#cbd5e1', fontSize: '0.78rem' }}>Koszty</div>
              <strong>{fmt(data?.costs_total)}</strong>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '0.7rem' }}>
              <div style={{ color: '#cbd5e1', fontSize: '0.78rem' }}>Rezerwacje</div>
              <strong>{selectedBookings}</strong>
            </div>
          </div>
        </div>

        <div style={{ background: decisionTone.bg, border: `1px solid ${decisionTone.border}`, color: decisionTone.text, borderRadius: 16, padding: '1rem', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.8rem', alignItems: 'center' }}>
              <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 800 }}>Najbliższa decyzja cenowa</div>
              <div style={{ width: 34, height: 34, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.7)', fontSize: '1.2rem', fontWeight: 900 }}>{decisionTone.icon}</div>
            </div>
            {primaryDecision ? (
              <>
                <div style={{ fontSize: '1.35rem', fontWeight: 900, marginTop: '0.65rem' }}>
                  {formatMonth(primaryDecision.period)}: {primaryDecision.action} {primaryDecision.price_delta_pct ? `${primaryDecision.price_delta_pct > 0 ? '+' : ''}${primaryDecision.price_delta_pct}%` : ''}
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.92rem', lineHeight: 1.45 }}>{primaryDecision.reason}</div>
              </>
            ) : (
              <div style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '0.65rem' }}>Brak decyzji cenowej do pokazania.</div>
            )}
          </div>
          {primaryDecision && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.45rem', marginTop: '1rem', fontSize: '0.82rem' }}>
              <div><strong>{primaryDecision.occupancy_pct}%</strong><br />zajętości</div>
              <div><strong>{primaryDecision.empty_days}</strong><br />pustych dni</div>
              <div><strong>{fmt(primaryDecision.adr)}</strong><br />ADR</div>
            </div>
          )}
        </div>
      </div>

      <div className="finance-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <article className="card" style={{ padding: '1.2rem' }}>
          <h3 style={{ marginTop: 0 }}>📈 Przychody</h3>
          <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{fmt(data?.revenue)}</div>
          <div style={{ color: '#dc2626', fontSize: '0.9rem' }}>
            Prowizja Booking.com: −{fmt(data?.commission)}
          </div>
          <hr style={{ margin: '8px 0', borderColor: '#e5e7eb' }} />
          <div style={{ fontWeight: 700 }}>Netto: {fmt(data?.revenue_net)}</div>
          {(data?.details?.bookings?.length > 0) && (
            <button className="secondary small" style={{ marginTop: '8px' }} onClick={() => setShowDetails(!showDetails)}>
              {showDetails ? 'Ukryj szczegóły ▲' : `Szczegóły (${data.details.bookings.length}) ▼`}
            </button>
          )}
          {showDetails && (data?.details?.bookings || []).map((b, i) => (
            <div key={i} style={{ fontSize: '0.82rem', padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
              {b.guest_name} · {b.checkin}→{b.checkout} · {fmt(b.price ?? b.total_amount ?? 0)}
            </div>
          ))}
        </article>

        <article className="card" style={{ padding: '1.2rem' }}>
          <h3 style={{ marginTop: 0 }}>💰 Koszty</h3>
          {(data?.details?.fixed_items || []).map((f, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', padding: '2px 0' }}>
              <span>{f.description || f.category}</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>−{fmt(f.amount)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', padding: '2px 0' }}>
            <span>Maria / sprzątanie — {cleaningJobCount} rezerwacji × {fmt(mariaCostPerBooking)}</span>
            <span style={{ fontWeight: 600, color: '#dc2626' }}>−{fmt(cleaningCost)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', padding: '2px 0' }}>
            <span>Materiały ({data?.details?.materials_count || 0})</span>
            <span style={{ fontWeight: 600, color: '#dc2626' }}>−{fmt(data?.materials)}</span>
          </div>
          <hr style={{ margin: '8px 0', borderColor: '#e5e7eb' }} />
          <div style={{ fontWeight: 700 }}>Razem: {fmt(data?.costs_total)}</div>
          <div className="hint" style={{ marginTop: '0.45rem' }}>Każda rezerwacja liczy 100 PLN kosztu Marii.</div>
          {user?.role === 'admin' && (
            <button className="secondary small" style={{ marginTop: '8px' }} onClick={openEdit}>
              Edytuj koszty stałe ✏️
            </button>
          )}
        </article>
      </div>

      <div className="finance-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
        <article className="card" style={{ padding: '1.2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>📚 Historia przychodu</h3>
              <p className="hint" style={{ margin: 0 }}>
                Widać tu poprzednie okresy: ile wpływało, ile zostawało i gdzie były większe dziury w kalendarzu.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              <button type="button" className={historyMode === 'months' ? 'primary small' : 'secondary small'} onClick={() => setHistoryMode('months')}>Miesiące</button>
              <button type="button" className={historyMode === 'quarters' ? 'primary small' : 'secondary small'} onClick={() => setHistoryMode('quarters')}>Kwartały</button>
              <button type="button" className={historyMode === 'years' ? 'primary small' : 'secondary small'} onClick={() => setHistoryMode('years')}>Lata</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem', marginTop: '1rem', marginBottom: '1rem' }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
              <div className="hint">Najlepszy okres</div>
              <div style={{ fontWeight: 800, marginTop: 4 }}>{bestRevenuePeriod ? formatPeriodLabel(bestRevenuePeriod.period, historyMode) : '—'}</div>
              <div style={{ color: '#166534', fontWeight: 700 }}>{bestRevenuePeriod ? fmt(bestRevenuePeriod.revenue) : '—'}</div>
            </div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
              <div className="hint">Średni przychód</div>
              <div style={{ fontWeight: 800, marginTop: 4 }}>
                {historyMode === 'months' ? fmt(averages.avg_monthly_revenue) : historyMode === 'quarters' ? fmt(averages.avg_quarterly_revenue) : fmt(averages.avg_yearly_revenue)}
              </div>
            </div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
              <div className="hint">Najsłabsze obłożenie</div>
              <div style={{ fontWeight: 800, marginTop: 4 }}>{worstOccupancyPeriod ? formatPeriodLabel(worstOccupancyPeriod.period, historyMode) : '—'}</div>
              <div style={{ color: '#9a3412', fontWeight: 700 }}>{worstOccupancyPeriod ? `${worstOccupancyPeriod.occupancy_pct}% zajętości` : '—'}</div>
            </div>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: '0.9rem', background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <strong>Wykres historii przychodu</strong>
              <span className="hint">Słupki pokazują przychód brutto w kolejnych {historyMode === 'months' ? 'miesiącach' : historyMode === 'quarters' ? 'kwartałach' : 'latach'}.</span>
            </div>
            {historyRows.length ? (
              <div style={{ overflowX: 'auto', paddingBottom: '0.35rem' }}>
                <div style={{ minWidth: `${Math.max(720, historyRows.length * 54)}px`, height: 260, display: 'flex', alignItems: 'flex-end', gap: 10, borderLeft: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', padding: '1.6rem 0.75rem 2.8rem', position: 'relative', background: 'repeating-linear-gradient(to top, transparent 0, transparent 49px, #e5e7eb 50px)' }}>
                  <div style={{ position: 'absolute', left: 10, top: 6, fontSize: '0.75rem', color: '#64748b' }}>{fmt(maxHistoryRevenue)}</div>
                  <div style={{ position: 'absolute', left: 10, bottom: 38, fontSize: '0.75rem', color: '#64748b' }}>0 PLN</div>
                  {historyRows.map((item, idx) => {
                    const height = Math.max(8, (Number(item.revenue || 0) / maxHistoryRevenue) * 180);
                    const isBest = bestRevenuePeriod?.period === item.period;
                    const showFullLabel = historyRows.length <= 14 || idx === 0 || idx === historyRows.length - 1 || idx % 3 === 0;
                    const label = historyMode === 'months' ? item.period.slice(2).replace('-', '.') : formatPeriodLabel(item.period, historyMode);
                    return (
                      <div key={item.period} title={`${formatPeriodLabel(item.period, historyMode)} · ${fmt(item.revenue)} · ${item.booked_days} nocy`} style={{ width: 44, height: 210, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', position: 'relative', flex: '0 0 44px' }}>
                        <div style={{ position: 'absolute', bottom: height + 28, fontSize: '0.7rem', color: isBest ? '#166534' : '#64748b', fontWeight: isBest ? 800 : 600, transform: 'rotate(-35deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
                          {isBest ? fmt(item.revenue).replace(' PLN', '') : ''}
                        </div>
                        <div style={{ width: 30, height, borderRadius: '8px 8px 0 0', background: item.net_income >= 0 ? (isBest ? '#16a34a' : '#2563eb') : '#dc2626', boxShadow: isBest ? '0 0 0 3px #bbf7d0' : 'none' }} />
                        <div style={{ position: 'absolute', bottom: -30, fontSize: '0.72rem', color: showFullLabel ? '#334155' : '#94a3b8', fontWeight: showFullLabel ? 700 : 400, transform: 'rotate(-35deg)', whiteSpace: 'nowrap' }}>
                          {showFullLabel ? label : '·'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="hint" style={{ marginTop: '0.4rem' }}>Przewiń poziomo, żeby zobaczyć cały zakres.</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.55rem' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#334155' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#2563eb' }} />miesiąc z zyskiem</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#334155' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#16a34a' }} />najlepszy okres</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#334155' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: '#dc2626' }} />miesiąc ze stratą</span>
                </div>
              </div>
            ) : <p className="hint" style={{ margin: 0 }}>Brak danych do wykresu.</p>}
          </div>

          <div style={{ display: 'grid', gap: '0.65rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong>Ostatnie okresy</strong>
              <span className="hint">Pokazuję ostatnie {visibleHistoryRows.length}; pełny zakres jest na wykresie powyżej.</span>
            </div>
            {visibleHistoryRows.map((item) => (
              <div key={item.period} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.8rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>{formatPeriodLabel(item.period, historyMode)}</strong>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span className="hint">Przychód: <strong style={{ color: '#111827' }}>{fmt(item.revenue)}</strong></span>
                    <span className="hint">Wynik: <strong style={{ color: item.net_income >= 0 ? '#166534' : '#991b1b' }}>{fmt(item.net_income)}</strong></span>
                  </div>
                </div>
                <div style={{ marginTop: '0.55rem', height: 10, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.max(6, (Number(item.revenue || 0) / maxHistoryRevenue) * 100)}%`, height: '100%', background: item.net_income >= 0 ? '#2563eb' : '#dc2626' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.5rem', marginTop: '0.65rem', fontSize: '0.88rem' }}>
                  <div><span className="hint">Rezerwacje</span><br /><strong>{item.bookings}</strong></div>
                  <div><span className="hint">Puste dni</span><br /><strong>{item.empty_days}</strong></div>
                  <div><span className="hint">Zajętość</span><br /><strong>{item.occupancy_pct}%</strong></div>
                  <div><span className="hint">Śr./dzień</span><br /><strong>{fmt(item.avg_revenue_per_booked_day)}</strong></div>
                </div>
              </div>
            ))}
            {!historyRows.length && <p className="hint">Brak historii do pokazania.</p>}
          </div>

          {coverage && (
            <p className="hint" style={{ marginTop: '0.9rem', marginBottom: 0 }}>
              Zakres analizy: {coverage.from} → {coverage.to} ({coverage.calendar_days} dni kalendarzowych).
            </p>
          )}
        </article>

        <article className="card" style={{ padding: '1.2rem' }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>🧭 Średnia, puste dni i szanse cenowe</h3>
          <p className="hint" style={{ marginTop: 0 }}>
            Ten kafelek pokazuje, ile średnio zarabiamy, ile mamy pustych dni i gdzie wzór się powtarza na tyle, że warto testować zmianę ceny.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
              <div className="hint">Średni przychód / miesiąc</div>
              <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(averages.avg_monthly_revenue)}</div>
            </div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
              <div className="hint">Średnio pustych dni / miesiąc</div>
              <div style={{ fontWeight: 800, marginTop: 4 }}>{averages.avg_empty_days_per_month || 0}</div>
            </div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
              <div className="hint">Średnio / zajęty dzień</div>
              <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(averages.avg_revenue_per_booked_day)}</div>
            </div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
              <div className="hint">Zajętość w całym zakresie</div>
              <div style={{ fontWeight: 800, marginTop: 4 }}>{averages.overall_occupancy_pct || 0}%</div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.8rem' }}>
              <strong>Okresy do obniżki / promocji</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.7rem' }}>
                {weakMonths.map((item) => {
                  const tone = toneForRecommendation(item.recommendation);
                  return <span key={item.period} style={{ padding: '0.45rem 0.6rem', borderRadius: 999, border: `1px solid ${tone.border}`, background: tone.bg, color: tone.text, fontSize: '0.88rem' }}>{formatMonth(item.period)} · {item.empty_days} pustych dni</span>;
                })}
                {!weakMonths.length && <span className="hint">Na razie brak wyraźnie słabych miesięcy.</span>}
              </div>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.8rem' }}>
              <strong>Dni tygodnia, które się powtarzają</strong>
              <div style={{ display: 'grid', gap: '0.55rem', marginTop: '0.7rem' }}>
                {[...weakWeekdays, ...strongWeekdays].slice(0, 6).map((item) => {
                  const tone = toneForRecommendation(item.recommendation);
                  return (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', border: `1px solid ${tone.border}`, background: tone.bg, color: tone.text, borderRadius: 10, padding: '0.6rem 0.75rem' }}>
                      <div>
                        <strong>{item.label}</strong>
                        <div style={{ fontSize: '0.82rem' }}>{item.booked_days} zajętych dni · śr. {fmt(item.avg_revenue_per_booked_day)}</div>
                      </div>
                      <strong style={{ fontSize: '0.85rem' }}>{item.recommendation}</strong>
                    </div>
                  );
                })}
                {!weakWeekdays.length && !strongWeekdays.length && <span className="hint">Za mało powtarzalnego wzoru, żeby uczciwie rekomendować zmianę ceny po dniu tygodnia.</span>}
              </div>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.8rem' }}>
              <strong>Wniosek operacyjny</strong>
              <ul style={{ margin: '0.7rem 0 0', paddingLeft: '1.1rem' }}>
                {(pricingPatterns.suggestions || []).map((text, idx) => <li key={idx} style={{ marginBottom: '0.35rem' }}>{text}</li>)}
                {!(pricingPatterns.suggestions || []).length && <li>Na obecnej historii nie widać jeszcze mocnego, powtarzalnego wzoru do zmiany cen.</li>}
              </ul>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.8rem' }}>
              <strong>Okresy, w których można bronić ceny</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.7rem' }}>
                {strongMonths.map((item) => {
                  const tone = toneForRecommendation(item.recommendation);
                  return <span key={item.period} style={{ padding: '0.45rem 0.6rem', borderRadius: 999, border: `1px solid ${tone.border}`, background: tone.bg, color: tone.text, fontSize: '0.88rem' }}>{formatMonth(item.period)} · {item.occupancy_pct}% zajętości</span>;
                })}
                {!strongMonths.length && <span className="hint">Na razie brak mocnego okresu, w którym dane jasno mówią: podnoś cenę.</span>}
              </div>
            </div>
          </div>
        </article>
      </div>

      <article className="card" style={{ padding: '1.2rem', marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>💸 System cenowy: kiedy podnieść, kiedy obniżyć</h3>
        <p className="hint" style={{ marginTop: 0 }}>
          Rekomendacje łączą historię portalu z raportami Booking.com: główne okno wyszukiwania to 8–30 dni, najczęstszy pobyt to 1 noc, a popyt w Szczecinie spadł o {Math.abs(Number(marketInsights.market_demand_change_pct || 0))}% r/r.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.35rem 0.55rem', borderRadius: 999, background: '#ecfdf5', border: '1px solid #86efac', color: '#166534', fontSize: '0.82rem', fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: '#16a34a' }} />podnieś / chroń</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.35rem 0.55rem', borderRadius: 999, background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', fontSize: '0.82rem', fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: '#f97316' }} />obniż / promocja</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.35rem 0.55rem', borderRadius: 999, background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155', fontSize: '0.82rem', fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: '#64748b' }} />trzymaj</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">ADR z Booking</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(marketInsights.sales_last_365_adr)}</div>
          </div>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">Przychód 365 dni</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(marketInsights.sales_last_365_revenue)}</div>
          </div>
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">Tempo przyszłe</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{marketInsights.future_room_nights || 0} nocy · {fmt(marketInsights.future_revenue)}</div>
          </div>
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">Rynek Szczecin</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{marketInsights.market_demand_change_pct || 0}% popytu</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.9rem' }}>
            <strong>Najbliższe decyzje cenowe</strong>
            <div style={{ display: 'grid', gap: '0.6rem', marginTop: '0.75rem' }}>
              {futurePricing.slice(0, 6).map((item) => {
                const up = item.price_delta_pct > 0;
                const down = item.price_delta_pct < 0;
                const color = up ? '#166534' : down ? '#9a3412' : '#334155';
                const bg = up ? '#ecfdf5' : down ? '#fff7ed' : '#f8fafc';
                const border = up ? '#86efac' : down ? '#fdba74' : '#cbd5e1';
                return (
                  <div key={item.period} style={{ border: `1px solid ${border}`, background: bg, borderRadius: 10, padding: '0.7rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                      <strong>{formatMonth(item.period)}</strong>
                      <strong style={{ color }}>{item.action} {item.price_delta_pct ? `${item.price_delta_pct > 0 ? '+' : ''}${item.price_delta_pct}%` : ''}</strong>
                    </div>
                    <div className="hint" style={{ marginTop: '0.35rem' }}>{item.occupancy_pct}% zajętości · {item.empty_days} pustych dni · ADR {fmt(item.adr)} · okno: {item.lead_bucket}</div>
                    <div style={{ marginTop: '0.35rem', fontSize: '0.88rem', color }}>{item.reason}</div>
                  </div>
                );
              })}
              {!futurePricing.length && <span className="hint">Brak przyszłych miesięcy do rekomendacji.</span>}
            </div>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.9rem' }}>
            <strong>Reguły operacyjne</strong>
            <ul style={{ margin: '0.75rem 0 0', paddingLeft: '1.1rem' }}>
              {pricingRules.map((rule, idx) => <li key={idx} style={{ marginBottom: '0.45rem' }}>{rule}</li>)}
            </ul>
            <div style={{ marginTop: '0.9rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.75rem' }}>
              <strong>Wniosek z Booking.com</strong>
              <div className="hint" style={{ marginTop: '0.35rem' }}>
                Najczęściej szukają: {marketInsights.dominant_country || '—'}, {marketInsights.dominant_traveller_type || '—'}, {marketInsights.dominant_stay_length || '—'}, mobile. W praktyce: krótkie promocje mobile / last minute mają sens głównie przy pustych dniach, a mocne terminy lepiej chronić ceną.
              </div>
            </div>
          </div>
        </div>
      </article>

      <article className="card" style={{ padding: '1.2rem', marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>🎯 Plan przychodu</h3>
        <p className="hint" style={{ marginTop: 0 }}>
          Ten kafelek pokazuje, ile brakuje do przeciętnego miesiąca i do ambitniejszego celu oraz czy obecny miesiąc broni kosztu Marii liczonego po 100 PLN za każdą rezerwację.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">Przychód teraz</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(selectedRevenue)}</div>
          </div>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">Cel bazowy</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(baselineTarget)}</div>
          </div>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">Cel ambitny</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(stretchTarget)}</div>
          </div>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <div className="hint">Netto po prowizji</div>
            <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(selectedRevenueNet)}</div>
          </div>
        </div>

        <div className="finance-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '1rem' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.9rem' }}>
            <strong>Postęp do celu</strong>
            <div style={{ marginTop: '0.8rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '0.35rem' }}>
                <span>Do średniego miesiąca</span>
                <strong>{fmt(selectedGapToAverage)} braku</strong>
              </div>
              <div style={{ height: 12, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${progressPct(selectedRevenue, baselineTarget)}%`, height: '100%', background: '#2563eb' }} />
              </div>
            </div>
            <div style={{ marginTop: '0.8rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '0.35rem' }}>
                <span>Do celu ambitnego</span>
                <strong>{fmt(selectedGapToStretch)} braku</strong>
              </div>
              <div style={{ height: 12, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${progressPct(selectedRevenue, stretchTarget)}%`, height: '100%', background: '#16a34a' }} />
              </div>
            </div>
            <ul style={{ margin: '0.9rem 0 0', paddingLeft: '1.1rem' }}>
              <li>Średnia wartość rezerwacji w wybranym miesiącu: {fmt(avgBookingValue)}</li>
              <li>Szacowany koszt Marii w tym miesiącu: {fmt(projectedMariaCost)} ({selectedBookings} × 100 PLN)</li>
              <li>Średni miesięczny koszt Marii z historii: {fmt(averages.expected_maria_cost_per_month)}</li>
            </ul>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.9rem' }}>
            <strong>Wniosek planistyczny</strong>
            <div style={{ display: 'grid', gap: '0.55rem', marginTop: '0.8rem' }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '0.75rem' }}>
                <div className="hint">Najlepszy miesiąc w historii</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>{fmt(revenuePlan.best_month_revenue)}</div>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '0.75rem' }}>
                <div className="hint">Cel bazowy to mniej więcej</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>{avgBookingValue ? (Math.ceil((selectedGapToAverage / avgBookingValue) * 10) / 10) : 0} rezerwacji o obecnej średniej</div>
              </div>
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '0.75rem' }}>
                <div className="hint">Cel ambitny to mniej więcej</div>
                <div style={{ fontWeight: 800, marginTop: 4 }}>{avgBookingValue ? (Math.ceil((selectedGapToStretch / avgBookingValue) * 10) / 10) : 0} rezerwacji o obecnej średniej</div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {editOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setEditOpen(false)}
        >
          <div
            style={{
              background: 'white', borderRadius: '14px', padding: '1.5rem',
              maxWidth: '420px', width: '90%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Koszty stałe — {formatMonth(month)}</h3>
            {editItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <label style={{ width: '90px', fontSize: '0.9rem', fontWeight: 600 }}>{item.label}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={item.amount}
                  onChange={(e) => {
                    const next = [...editItems];
                    next[i].amount = e.target.value;
                    setEditItems(next);
                  }}
                  style={{ flex: 1, padding: '8px' }}
                  placeholder="0.00"
                />
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>PLN</span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
              <button className="primary" onClick={saveEdit} disabled={editSaving} style={{ flex: 1 }}>
                {editSaving ? 'Zapisuję...' : 'Zapisz'}
              </button>
              <button className="secondary" onClick={() => setEditOpen(false)}>Anuluj</button>
            </div>
            <p className="hint" style={{ marginTop: '0.7rem', marginBottom: 0, fontSize: '0.82rem' }}>
              To są wartości startowe na ten miesiąc — możesz je zmienić w każdym miesiącu osobno.
            </p>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .finance-grid { grid-template-columns: 1fr !important; }
          .finance-hero { grid-template-columns: 1fr !important; }
          .finance-kpi-row { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .finance-hero { padding: 0.75rem !important; }
          .finance-hero [style*="font-size: 2.8rem"] { font-size: 2.1rem !important; }
        }
      `}</style>
    </section>
  );
}
